import { useCallback, useReducer, useState } from 'react';
import { useCatalogService } from '../services/catalogService';
import { useSnackbar, type SnackbarState } from '../../../shared/hooks/useSnackbar';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';

export interface SubscribeFormState {
  accountId: string;
  services: string[];
  regions: string[];
  includeServiceComponents: string[];
}

const INITIAL_FORM: SubscribeFormState = {
  accountId: '',
  services: [],
  regions: [],
  includeServiceComponents: [],
};

export type SubscribeFormAction =
  | { type: 'set_account'; accountId: string }
  | { type: 'set_services'; services: string[] }
  | { type: 'set_regions'; regions: string[] }
  | { type: 'set_include_products'; includeServiceComponents: string[] }
  | { type: 'reset' };

function reducer(state: SubscribeFormState, action: SubscribeFormAction): SubscribeFormState {
  switch (action.type) {
    case 'set_account':
      return { ...state, accountId: action.accountId };
    case 'set_services':
      return { ...state, services: action.services };
    case 'set_regions':
      return { ...state, regions: action.regions };
    case 'set_include_products':
      return { ...state, includeServiceComponents: action.includeServiceComponents };
    case 'reset':
      return INITIAL_FORM;
  }
}

export interface PreviewDeployment {
  product_id: string;
  product_name: string;
  service: string;
  region: string;
  is_reactivation: boolean;
}

export interface SubscribePreview {
  customer_id: string;
  account_id: string;
  deployments_to_create: PreviewDeployment[];
  portfolios_to_share: Array<{ portfolio: string; regions: string[] }>;
  deployment_count: number;
  portfolio_count: number;
}

interface UseSubscribeFlowOptions {
  customerId: string;
  onSubscribed: () => void;
}

export interface UseSubscribeFlowResult {
  form: SubscribeFormState;
  dispatch: (action: SubscribeFormAction) => void;
  preview: SubscribePreview | null;
  previewLoading: boolean;
  previewError: string | null;
  runPreview: () => Promise<void>;
  submit: () => Promise<void>;
  submitting: boolean;
  canPreview: boolean;
  canSubmit: boolean;
  reset: () => void;
  snackbar: SnackbarState;
  closeSnackbar: () => void;
}

export function useSubscribeFlow({
  customerId,
  onSubscribed,
}: UseSubscribeFlowOptions): UseSubscribeFlowResult {
  const catalogService = useCatalogService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [form, dispatch] = useReducer(reducer, INITIAL_FORM);
  const [preview, setPreview] = useState<SubscribePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canPreview =
    !previewLoading &&
    form.accountId !== '' &&
    form.services.length > 0;

  const canSubmit = !submitting && preview !== null && canPreview;

  const runPreview = useCallback(async () => {
    if (!canPreview) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await catalogService.previewSubscribe({
        customer_id: customerId,
        account_id: form.accountId,
        services: form.services,
      });
      setPreview(result);
    } catch (err) {
      setPreview(null);
      setPreviewError(extractErrorMessage(err, 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  }, [canPreview, catalogService, customerId, form.accountId, form.services]);

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await catalogService.subscribe({
        customer_id: customerId,
        account_id: form.accountId,
        services: form.services,
        ...(form.regions.length > 0 && { regions: form.regions }),
        ...(form.includeServiceComponents.length > 0 && { include_products: form.includeServiceComponents }),
      });
      showSnackbar('Subscribed — reconciler will provision shortly');
      onSubscribed();
      dispatch({ type: 'reset' });
      setPreview(null);
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to subscribe'), 'error');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, catalogService, customerId, form, onSubscribed, showSnackbar]);

  const reset = useCallback(() => {
    dispatch({ type: 'reset' });
    setPreview(null);
    setPreviewError(null);
  }, []);

  return {
    form,
    dispatch,
    preview,
    previewLoading,
    previewError,
    runPreview,
    submit,
    submitting,
    canPreview,
    canSubmit,
    reset,
    snackbar,
    closeSnackbar,
  };
}
